import {
  Brightness4,
  Brightness7,
  CloudDownload as CloudDownloadIcon,
  Download as DownloadIcon,
  Logout,
  SettingsOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../components/Button";
import CustomDatePicker from "../components/CustomDatePicker";
import Title from "../components/PageTitle";
import SearchableDropdown from "../components/SearchableDropdown";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

// One row of the settings list: an icon, a title + description, and a control on the right.
const SettingsRow = ({ icon, iconColor, title, description, control }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      flexDirection: { xs: "column", sm: "row" },
      gap: 2,
      py: 2.5,
      px: 3,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
      <Avatar
        sx={{
          bgcolor: (theme) => alpha(iconColor || theme.palette.primary.main, 0.12),
          color: iconColor || "primary.main",
          width: 44,
          height: 44,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "auto" } }}>
      {control}
    </Box>
  </Box>
);

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      fontWeight: 700,
      letterSpacing: 1.2,
      color: "text.secondary",
      display: "block",
      px: 3,
      pt: 3,
    }}
  >
    {children}
  </Typography>
);

const Settings = ({ themeMode, toggleTheme }) => {
  const token = localStorage.getItem("token");
  const entityId = localStorage.getItem("entityId");
  const navigate = useNavigate();
  const Toast = useToast();

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
    }
  }, [navigate, token]);

  // Export patients dialog state
  const [allClinic, setAllClinics] = useState([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportClinics, setExportClinics] = useState([]);
  const [exportStartDate, setExportStartDate] = useState(null);
  const [exportEndDate, setExportEndDate] = useState(null);
  const [includeDiseases, setIncludeDiseases] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isExportCalendarOpen, setIsExportCalendarOpen] = useState(false);

  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const res = await apiService.fetchDoctorAndClinics(entityId);
        setAllClinics(res.data.clinics || []);
      } catch (error) {
        Toast.error("Error while fetching clinics");
      }
    };
    if (entityId) fetchClinics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const closeExportDialog = () => {
    setExportDialogOpen(false);
    setIsExportCalendarOpen(false);
  };

  const handleExportPatients = async () => {
    setExportLoading(true);
    try {
      const clinicIds = exportClinics.map((clinic) => clinic._id);
      const formattedStart = exportStartDate
        ? exportStartDate.toISOString().split("T")[0]
        : undefined;
      const formattedEnd = exportEndDate
        ? exportEndDate.toISOString().split("T")[0]
        : undefined;

      await apiService.exportPatientsData(
        token,
        entityId,
        clinicIds,
        formattedStart,
        formattedEnd,
        includeDiseases
      );
      Toast.success("Export downloaded successfully");
      closeExportDialog();
    } catch (error) {
      Toast.error("Error while exporting patients data");
    } finally {
      setExportLoading(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("entityId");
    sessionStorage.clear();
    window.location.replace("/login");
  };

  return (
    <Paper sx={{ padding: 2 }}>
      <Title text="Settings" icon={<SettingsOutlined />} />

      <Box sx={{ display: "flex", justifyContent: "center", py: { xs: 1, sm: 3 } }}>
        <Paper
          variant="outlined"
          sx={{
            width: "100%",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {/* Appearance */}
          <SectionLabel>Appearance</SectionLabel>
          <SettingsRow
            icon={themeMode === "light" ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
            title="Dark Mode"
            description={`Currently using the ${themeMode} theme`}
            control={
              <Switch
                checked={themeMode === "dark"}
                onChange={toggleTheme}
                color="primary"
                inputProps={{ "aria-label": "Toggle dark mode" }}
              />
            }
          />

          <Divider />

          {/* Data */}
          <SectionLabel>Data</SectionLabel>
          <SettingsRow
            icon={<CloudDownloadIcon fontSize="small" />}
            title="Export Patients"
            description="Download patient records for selected clinics and a date range as an Excel file"
            control={
              <CustomButton
                text="Export"
                startIcon={<DownloadIcon />}
                onClick={() => setExportDialogOpen(true)}
              />
            }
          />

          <Divider />

          {/* Account */}
          <SectionLabel>Account</SectionLabel>
          <SettingsRow
            icon={<Logout fontSize="small" />}
            iconColor="#d32f2f"
            title="Log out"
            description="Sign out of your account on this device"
            control={
              <Button
                variant="outlined"
                color="error"
                startIcon={<Logout />}
                onClick={onLogout}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              >
                Logout
              </Button>
            }
          />
        </Paper>
      </Box>

      <Dialog
        open={exportDialogOpen}
        onClose={() => (!exportLoading ? closeExportDialog() : null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            overflow: "visible",
            minHeight: isExportCalendarOpen ? 560 : "auto",
            transition: "min-height 0.2s ease",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Export Patients</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          <Box display="flex" flexDirection="column" gap={3} mt={1}>
            <SearchableDropdown
              width={"100%"}
              label="Clinics"
              options={allClinic}
              selectedOptions={exportClinics}
              onChange={setExportClinics}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option._id}
            />

            <Box display="flex" gap={2}>
              <CustomDatePicker
                selectedDate={exportStartDate}
                handleDateChange={(date) => setExportStartDate(date)}
                label={"From Date"}
                isDocEditable={true}
                onCalendarOpen={() => setIsExportCalendarOpen(true)}
                onCalendarClose={() => setIsExportCalendarOpen(false)}
              />
              <CustomDatePicker
                selectedDate={exportEndDate}
                handleDateChange={(date) => setExportEndDate(date)}
                label={"To Date"}
                isDocEditable={true}
                onCalendarOpen={() => setIsExportCalendarOpen(true)}
                onCalendarClose={() => setIsExportCalendarOpen(false)}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={includeDiseases}
                  onChange={(e) => setIncludeDiseases(e.target.checked)}
                />
              }
              label="Enable Disease (include Disease Count & Diseases columns)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            backgroundColor="#CCD3DA"
            color="#000"
            onClick={closeExportDialog}
            disabled={exportLoading}
          />
          <CustomButton
            text={exportLoading ? "Exporting..." : "Export"}
            onClick={handleExportPatients}
            disabled={exportLoading}
          />
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Settings;
